'use client';

import { c as _c } from "react/compiler-runtime";
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { ProposalsHowItWorksModal } from '@/components/how-it-works/proposals-how-it-works-modal';
import { WhatIsAProposalModal } from '@/components/how-it-works/what-is-a-proposal-modal';
import { ListingDiscoveryActions } from '@/components/shared/listing-discovery-actions';
import { AnimatedButton } from '@/components/ui/animated-button';
import { HelpCircleIconSolid, PlusSignIcon } from '@/components/ui/icon/glyphs';
import { onEvent } from '@/events';
import { useAuthGate } from '@/hooks/use-auth-gate';
import { type ProposalAuthContinuation, type ProposalAuthContinuationFailureReason, readProposalAuthContinuation } from '@/lib/launchpad/proposal-auth-continuation';
import { logger } from '@/lib/telemetry/logger';
import { LISTING_INDEX_PAGE_STACK_CLASS } from '@/lib/ui/layout';
import { cn } from '@/lib/ui/utils';
import { CreateProposalDialog } from './create-proposal-dialog';
import { LaunchpadHeader } from './launchpad-header';
import { ProposalFeed } from './proposal-feed';

// The launch / help / what-is dialogs are mutually-exclusive modal overlays —
// one discriminated state models them better than three parallel flags.
type ActiveModal = {
  kind: 'create';
  continuation?: ProposalAuthContinuation;
  continuationFailure?: ProposalAuthContinuationFailureReason;
} | {
  kind: 'help';
} | {
  kind: 'whatIs';
};
export function LaunchpadFeedLayout() {
  const $ = _c(57);
  const t = useTranslations("launchpad.feed");
  const searchParams = useSearchParams();
  const [filterOpen, setFilterOpen] = useState(false);
  let t0;
  if ($[0] !== searchParams) {
    t0 = () => searchParams.get("create") === "1" ? {
      kind: "create"
    } : null;
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [activeModal, setActiveModal] = useState(t0);
  let t1;
  if ($[2] !== searchParams) {
    t1 = () => searchParams.get("view") === "watchlist";
    $[2] = searchParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const [watchlistActive, setWatchlistActive] = useState(t1);
  const continuationCheckedRef = useRef(false);
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setActiveModal(null);
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const closeModal = t2;
  let t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (continuationCheckedRef.current) {
        return;
      }
      continuationCheckedRef.current = true;
      const frameId = window.requestAnimationFrame(() => {
        const result = readProposalAuthContinuation();
        if (result.status === "ready") {
          setActiveModal({
            kind: "create",
            continuation: result.continuation
          });
          return;
        }
        if (result.status === "stale") {
          logger.warn("proposal_creation.auth_resume_failed", {
            reason: result.reason
          });
          onEvent("proposal_auth_continuation_failed", {
            flow: result.flow,
            sourceStep: 4,
            outcome: result.reason,
            resumeMechanism: "session_remount",
            elapsedMs: result.createdAt === null ? 0 : Math.max(0, Date.now() - result.createdAt)
          });
          setActiveModal({
            kind: "create",
            continuationFailure: result.reason
          });
        }
      });
      return () => window.cancelAnimationFrame(frameId);
    };
    t4 = [];
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  const {
    ensureAuthenticated
  } = useAuthGate();
  let t5;
  if ($[7] !== ensureAuthenticated || $[8] !== watchlistActive) {
    t5 = function handleWatchlistToggle() {
      if (!ensureAuthenticated()) {
        return;
      }
      const next = !watchlistActive;
      setWatchlistActive(next);
      onEvent("proposal_watchlist_toggled", {
        active: next
      });
    };
    $[7] = ensureAuthenticated;
    $[8] = watchlistActive;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleWatchlistToggle = t5;
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setFilterOpen(true);
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setActiveModal({
      kind: "create"
    });
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = activeModal?.kind === "create";
  let t9;
  if ($[12] !== t) {
    t9 = t("launchCta");
    $[12] = t;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t8 || $[15] !== t9) {
    t10 = <AnimatedButton variant="primary" prefix={PlusSignIcon} onClick={t7} aria-haspopup="dialog" aria-expanded={t8}>{t9}</AnimatedButton>;
    $[14] = t8;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => setActiveModal({
      kind: "help"
    });
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t) {
    t12 = t("helpAriaLabel");
    $[18] = t;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const t13 = activeModal?.kind === "help";
  const t14 = activeModal?.kind === "help" && "bg-background-tertiary";
  let t15;
  if ($[20] !== t14) {
    t15 = cn("text-foreground-tertiary hover:bg-background-tertiary", t14);
    $[20] = t14;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== t12 || $[23] !== t13 || $[24] !== t15) {
    t16 = <AnimatedButton variant="tertiary" size="default" prefix={HelpCircleIconSolid} onClick={t11} aria-label={t12} aria-haspopup="dialog" aria-expanded={t13} className={t15} />;
    $[22] = t12;
    $[23] = t13;
    $[24] = t15;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== filterOpen || $[27] !== handleWatchlistToggle || $[28] !== t10 || $[29] !== t16 || $[30] !== watchlistActive) {
    t17 = <ListingDiscoveryActions filterOpen={filterOpen} onFilterClick={t6} watchlistActive={watchlistActive} onWatchlistClick={handleWatchlistToggle} creationAction={t10}>{t16}</ListingDiscoveryActions>;
    $[26] = filterOpen;
    $[27] = handleWatchlistToggle;
    $[28] = t10;
    $[29] = t16;
    $[30] = watchlistActive;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  const actions = t17;
  const t18 = activeModal?.kind === "create";
  let t19;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => setActiveModal({
      kind: "create"
    });
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== actions || $[34] !== t18) {
    t20 = <LaunchpadHeader actions={actions} creationOpen={t18} onLaunchMarketClick={t19} />;
    $[33] = actions;
    $[34] = t18;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== filterOpen || $[37] !== watchlistActive) {
    t21 = <ProposalFeed showCreateButton={true} filterOpen={filterOpen} onFilterOpenChange={setFilterOpen} watchlistActive={watchlistActive} onWatchlistActiveChange={setWatchlistActive} />;
    $[36] = filterOpen;
    $[37] = watchlistActive;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  const t22 = activeModal?.kind === "create";
  const t23 = activeModal?.kind === "create" ? activeModal.continuation : undefined;
  const t24 = activeModal?.kind === "create" ? activeModal.continuationFailure : undefined;
  let t25;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = isOpen => {
      if (!isOpen) {
        closeModal();
      }
    };
    $[39] = t25;
  } else {
    t25 = $[39];
  }
  let t26;
  if ($[40] !== t22 || $[41] !== t23 || $[42] !== t24) {
    t26 = <CreateProposalDialog open={t22} continuation={t23} continuationFailure={t24} onOpenChange={t25} />;
    $[40] = t22;
    $[41] = t23;
    $[42] = t24;
    $[43] = t26;
  } else {
    t26 = $[43];
  }
  const t27 = activeModal?.kind === "help";
  let t28;
  let t29;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = isOpen_0 => {
      if (!isOpen_0) {
        closeModal();
      }
    };
    t29 = () => setActiveModal({
      kind: "whatIs"
    });
    $[44] = t28;
    $[45] = t29;
  } else {
    t28 = $[44];
    t29 = $[45];
  }
  let t30;
  if ($[46] !== t27) {
    t30 = <ProposalsHowItWorksModal open={t27} onOpenChange={t28} onWhatIsProposal={t29} />;
    $[46] = t27;
    $[47] = t30;
  } else {
    t30 = $[47];
  }
  const t31 = activeModal?.kind === "whatIs";
  let t32;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = isOpen_1 => {
      if (!isOpen_1) {
        closeModal();
      }
    };
    $[48] = t32;
  } else {
    t32 = $[48];
  }
  let t33;
  if ($[49] !== t31) {
    t33 = <WhatIsAProposalModal open={t31} onOpenChange={t32} />;
    $[49] = t31;
    $[50] = t33;
  } else {
    t33 = $[50];
  }
  let t34;
  if ($[51] !== t20 || $[52] !== t21 || $[53] !== t26 || $[54] !== t30 || $[55] !== t33) {
    t34 = <div className={LISTING_INDEX_PAGE_STACK_CLASS}>{t20}{t21}{t26}{t30}{t33}</div>;
    $[51] = t20;
    $[52] = t21;
    $[53] = t26;
    $[54] = t30;
    $[55] = t33;
    $[56] = t34;
  } else {
    t34 = $[56];
  }
  return t34;
}